import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

/**
 * Represents an anxiety survey filled.
 */
export default class AnxietyFilledJournalEvent extends JournalEvent {
  static type = "anxiety-filled-journal-event";

  @attr("number") declare willingness: number;

  @attr("number") declare nervous: number;

  @attr("number") declare happyMyself: number;

  @attr("number") declare happinessOthers: number;

  @attr("number") declare fail: number;

  @attr("number") declare rested: number;

  @attr("number") declare calm: number;

  @attr("number") declare overcome: number;

  @attr("number") declare overreact: number;

  @attr("number") declare happy: number;

  @attr("number") declare troubledThoughts: number;

  @attr("number") declare confidence: number;

  @attr("number") declare security: number;

  @attr("number") declare decisions: number;

  @attr("number") declare equalTo: number;

  @attr("number") declare satisfaction: number;

  @attr("number") declare ideasWithoutImportance: number;

  @attr("number") declare disappointment: number;

  @attr("number") declare solidNerves: number;

  @attr("number") declare preoccupied: number;

  @attr("string") declare originTaskIdentifier: string;

  @attr("number") declare score: number;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.anxiety-filled-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      willingness: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.willingness"
        )
      }),
      nervous: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.nervous")
      }),
      happyMyself: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.happy-myself"
        )
      }),
      happinessOthers: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.happiness-others"
        )
      }),
      fail: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.fail")
      }),
      rested: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.rested")
      }),
      calm: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.calm")
      }),
      overcome: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.overcome")
      }),
      overreact: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.overreact"
        )
      }),
      happy: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.happy")
      }),
      troubledThoughts: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.troubled-thoughts"
        )
      }),
      confidence: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.confidence"
        )
      }),
      security: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.security")
      }),
      decisions: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.decisions"
        )
      }),
      equalTo: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.equal-to")
      }),
      satisfaction: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.satisfaction"
        )
      }),
      ideasWithoutImportance: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.ideas-without-importance"
        )
      }),
      disappointment: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.disappointment"
        )
      }),
      solidNerves: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.solid-nerves"
        )
      }),
      preoccupied: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.anxiety-filled-journal-event.preoccupied"
        )
      }),
      score: validateNumber({
        allowBlank: true,
        description: intl.t("validations.anxiety-filled-journal-event.score")
      })
    };
  }

  //---- End of generated code ----

  static states = [
    "willingness",
    "nervous",
    "happyMyself",
    "happinessOthers",
    "fail",
    "rested",
    "calm",
    "overcome",
    "overreact",
    "happy",
    "troubledThoughts",
    "confidence",
    "security",
    "decisions",
    "equalTo",
    "satisfaction",
    "ideasWithoutImportance",
    "disappointment",
    "solidNerves",
    "preoccupied"
  ];

  @belongsTo("patient", {
    async: false,
    inverse: "anxietyFilledJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "anxiety-filled-journal-event": AnxietyFilledJournalEvent;
  }
}
